<div class="setup-container view">
    @if (isLoading) {
        <div class="loading">
            <mat-spinner color="accent" diameter="40"></mat-spinner>
            <p>Loading setup status...</p>
        </div>
    }

    @if (error) {
        <div class="error-message">
            <mat-icon>error</mat-icon>
            <span>{{ error }}</span>
        </div>
    }

    @if (!isLoading) {
        <mat-card class="cardPlain">
            <div class="custom-card-header">
                <div class="section-header">
                    <app-breadcrumbs [items]="breadcrumbs"></app-breadcrumbs>
                    <h1 class="m-0 f-w-500">HubSpot Integration Readiness</h1>
                    <p class="m-0 help-text">Review and repair the checks required for Shine Enrich operations.</p>
                </div>
            </div>
            <mat-card-content class="d-flex flex-col gap-24">
                @if (readiness.runtimeUnavailable) {
                    <section class="readiness-panel" data-testid="enrich-runtime-unavailable">
                        <div class="readiness-panel__header">
                            <div>
                                <h2 class="m-0 f-s-18 f-w-600">Enrich runtime unavailable</h2>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Enrich status cannot be confirmed right now. No setup actions are available.
                                    Review <a [routerLink]="['/enrich/runtime-status']">Runtime Status</a> and try again later.
                                </p>
                            </div>
                        </div>
                    </section>
                } @else {
                <section class="readiness-panel" [class.readiness-panel--ready]="readiness.isOperationalReady">
                    <div class="readiness-panel__header">
                        <div>
                            <h2 class="m-0 f-s-18 f-w-600">
                                {{ readiness.isOperationalReady ? 'Operationally ready' : 'Action needed' }}
                            </h2>
                            <p class="m-0 f-s-14 text-gray-100">
                                {{ readiness.isOperationalReady ? 'All blocking readiness checks are complete.' : 'Complete the blocking checks below before using operational Enrich pages.' }}
                            </p>
                        </div>
                    </div>
                    <div class="readiness-grid">
                        @for (item of readinessDimensions; track item.key) {
                            <article class="readiness-item readiness-item--{{ item.status }}">
                                <mat-icon>{{ readinessIcon(item.key) }}</mat-icon>
                                <div class="readiness-item__body">
                                    <div class="readiness-item__title">
                                        <span>{{ item.label }}</span>
                                        <span class="readiness-item__status">{{ readinessStatusLabel(item.key) }}</span>
                                    </div>
                                    <p class="m-0 f-s-13 text-gray-100">{{ item.message }}</p>
                                </div>
                            </article>
                        }
                    </div>
                </section>
                <mat-horizontal-stepper
                        (selectionChange)="onStepperChange($event)"
                        [selectedIndex]="selectedStepIndex"
                        class="setup-stepper"
                >
                    <mat-step [completed]="isDimensionReady('connection') && isDimensionReady('scopes')" [editable]="true">
                        <ng-template matStepLabel>Authentication</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Connect to HubSpot</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    First, you need to connect your HubSpot portal to Shine Enrich. This will install a
                                    new public app in
                                    your HubSpot portal and allow Shine to access your data.
                                </p>
                            </div>
                            @if (hubspotConnections.length > 1) {
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>HubSpot connection</mat-label>
                                    <mat-select
                                            (selectionChange)="onHubspotConnectionChange($event.value)"
                                            [value]="selectedHubspotConnectionId"
                                            [disabled]="isProcessingAuth"
                                    >
                                        @for (conn of hubspotConnections; track conn.id) {
                                            <mat-option [value]="conn.id">
                                                {{ conn.name || conn.id }}
                                                @if (conn.connected || conn.oauth_connected) {
                                                    (connected)
                                                }
                                            </mat-option>
                                        }
                                    </mat-select>
                                </mat-form-field>
                            }
                            @if (selectedHubspotConnectionId && isCheckingConnectionPermissions) {
                                <p class="m-0 f-s-12 text-gray-100">Checking HubSpot connection permissions...</p>
                            }
                            @if (connectionPermissionError) {
                                <div class="error-message">
                                    <mat-icon>error</mat-icon>
                                    <span>{{ connectionPermissionError }}</span>
                                </div>
                            }
                            <div class="action-buttons action-buttons--start">
                                <button
                                        (click)="initiateAuth()"
                                        [disabled]="isProcessingAuth || isCheckingConnectionPermissions"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-flat-button
                                        type="button"
                                >
                                    <mat-icon>link</mat-icon>
                                    Connect to HubSpot
                                </button>
                                @if (isProcessingAuth) {
                                    <mat-spinner diameter="24"></mat-spinner>
                                }
                            </div>
                            @if (isDimensionReady('connection') && isDimensionReady('scopes')) {
                                <div class="banner">
                                    <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                    <div class="content d-flex flex-col gap-4">
                                        <div class="title">Connected to HubSpot successfully</div>
                                    </div>
                                </div>
                            }
                        </section>
                    </mat-step>
                    <mat-step [completed]="isDimensionReady('settings')" [editable]="true">
                        <ng-template matStepLabel>Settings</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Configure settings</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Configure your Shine Enrich settings, including the CRM organization number and
                                    subunit fields.
                                </p>
                            </div>
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Connection property scope</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Limit which HubSpot properties this selected connection is allowed to create and sync.
                                    Leave empty to allow all mapped properties.
                                </p>
                            </div>
                            @if (selectedHubspotConnectionId) {
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>Allowed HubSpot properties for this connection</mat-label>
                                    <mat-select
                                            [value]="selectedHubspotProfileAllowedPropertyNames"
                                            (selectionChange)="onConnectionProfileAllowlistChange($event.value)"
                                            multiple
                                    >
                                        @for (prop of hubspotProperties; track prop.name) {
                                            <mat-option [value]="prop.name">
                                                {{ prop.label }} ({{ prop.name }})
                                            </mat-option>
                                        }
                                    </mat-select>
                                    <mat-hint>Leave empty to allow all mapped properties.</mat-hint>
                                </mat-form-field>
                                <div class="action-buttons action-buttons--start">
                                    <button
                                            (click)="saveConnectionPropertyScope()"
                                            [disabled]="isSavingPropertyScope"
                                            class="d-inline-flex align-items-center gap-6"
                                            color="primary"
                                            mat-stroked-button
                                            type="button"
                                    >
                                        <mat-icon>rule</mat-icon>
                                        Save property scope
                                    </button>
                                    @if (isSavingPropertyScope) {
                                        <mat-spinner diameter="24"></mat-spinner>
                                    }
                                </div>
                                @if (propertyScopeError) {
                                    <div class="error-message">
                                        <mat-icon>error</mat-icon>
                                        <span>{{ propertyScopeError }}</span>
                                    </div>
                                }
                                @if (propertyScopeMessage) {
                                    <div class="success-message">
                                        <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                        <div class="content d-flex flex-col gap-4">
                                            <p class="m-0 f-s-14">{{ propertyScopeMessage }}</p>
                                        </div>
                                    </div>
                                }
                            }
                            <form [formGroup]="settingsForm" class="d-flex flex-col gap-16 form-no-subscripts">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>CRM organization number property</mat-label>
                                    <input
                                            [matAutocomplete]="orgNumberAuto"
                                            formControlName="crmOrgNumberProperty"
                                            matInput
                                            placeholder="Search properties..."
                                            required
                                            type="text"
                                    />
                                    <mat-autocomplete #orgNumberAuto="matAutocomplete"
                                                      [displayWith]="displayHubspotProperty">
                                        @for (prop of filterHubspotProperties(settingsForm.get('crmOrgNumberProperty')?.value); track prop) {
                                            <mat-option
                                                    [value]="prop.name"
                                            >
                                                {{ prop.label }}
                                            </mat-option>
                                        }
                                    </mat-autocomplete>
                                    <mat-hint>The HubSpot property that stores the organization number</mat-hint>
                                </mat-form-field>
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>CRM subunit number property</mat-label>
                                    <input
                                            [matAutocomplete]="subUnitAuto"
                                            formControlName="crmSubUnitNumberProperty"
                                            matInput
                                            placeholder="Search properties..."
                                            type="text"
                                    />
                                    <mat-autocomplete #subUnitAuto="matAutocomplete"
                                                      [displayWith]="displayHubspotProperty">
                                        @for (prop of filterHubspotProperties(settingsForm.get('crmSubUnitNumberProperty')?.value); track prop) {
                                            <mat-option
                                                    [value]="prop.name"
                                            >
                                                {{ prop.label }}
                                            </mat-option>
                                        }
                                    </mat-autocomplete>
                                    <mat-hint>The HubSpot property that stores the subunit number</mat-hint>
                                </mat-form-field>
                            </form>
                            <div class="action-buttons action-buttons--start">
                                <button
                                        (click)="setupSettings()"
                                        [disabled]="isProcessingSettings || !isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-flat-button
                                        type="button"
                                >
                                    <mat-icon>save</mat-icon>
                                    Save settings
                                </button>
                                <button
                                        [disabled]="!isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        [queryParams]="{setup: 'true'}"
                                        [routerLink]="['/enrich/settings']"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-stroked-button
                                        type="button"
                                >
                                    <mat-icon>settings</mat-icon>
                                    Advanced settings
                                </button>
                                @if (isProcessingSettings) {
                                    <mat-spinner diameter="24"></mat-spinner>
                                }
                            </div>
                            @if (isDimensionReady('settings')) {
                                <div class="banner">
                                    <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                    <div class="content d-flex flex-col gap-4">
                                        <div class="title">Settings configured successfully</div>
                                    </div>
                                </div>
                            }
                        </section>
                    </mat-step>
                    <mat-step [completed]="isDimensionReady('webhooks')" [editable]="true">
                        <ng-template matStepLabel>Webhooks</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Set up webhooks</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Shine Enrich needs to set up webhook subscriptions for company creation events in
                                    HubSpot. This allows
                                    automatic enrichment of new companies.
                                </p>
                            </div>
                            <div class="action-buttons action-buttons--start">
                                <button
                                        (click)="setupWebhooks()"
                                        [disabled]="isProcessingWebhooks || !isDimensionReady('settings') || !isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-flat-button
                                        type="button"
                                >
                                    <mat-icon>notifications_active</mat-icon>
                                    Set up webhooks
                                </button>
                                @if (isProcessingWebhooks) {
                                    <mat-spinner diameter="24"></mat-spinner>
                                }
                            </div>
                            @if (isDimensionReady('webhooks')) {
                                <div class="banner">
                                    <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                    <div class="content d-flex flex-col gap-4">
                                        <div class="title">Webhooks set up successfully</div>
                                    </div>
                                </div>
                            }
                        </section>
                    </mat-step>
                    <mat-step [completed]="isDimensionReady('properties')" [editable]="true">
                        <ng-template matStepLabel>Properties</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Create HubSpot properties</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Shine Enrich needs to create custom properties in your HubSpot portal to store Brreg
                                    data. This step will
                                    create all the necessary properties.
                                </p>
                            </div>
                            <div class="action-buttons action-buttons--start">
                                <button
                                        (click)="setupProperties()"
                                        [disabled]="isProcessingProperties || !isDimensionReady('webhooks') || !isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-flat-button
                                        type="button"
                                >
                                    <mat-icon>add_circle</mat-icon>
                                    Create properties
                                </button>
                                @if (isProcessingProperties) {
                                    <mat-spinner diameter="24"></mat-spinner>
                                }
                            </div>
                            @if (isDimensionReady('properties')) {
                                <div class="banner">
                                    <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                    <div class="content d-flex flex-col gap-4">
                                        <div class="title">HubSpot properties created successfully</div>
                                    </div>
                                </div>
                            }
                        </section>
                    </mat-step>
                    <mat-step [completed]="isDimensionReady('mapping')" [editable]="true">
                        <ng-template matStepLabel>Mapping</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Set up field mapping</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Set up the mapping between Brreg fields and HubSpot properties. This determines
                                    which Brreg data will be
                                    synced to which HubSpot properties.
                                </p>
                            </div>
                            <div class="action-buttons action-buttons--start">
                                <button
                                        [disabled]="!isDimensionReady('properties') || !isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        [queryParams]="{setup: 'true'}"
                                        [routerLink]="['/enrich/mapping']"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-flat-button
                                        type="button"
                                >
                                    <mat-icon>compare_arrows</mat-icon>
                                    Go to mapping setup
                                </button>
                                <button
                                        (click)="skipMappingStep()"
                                        [disabled]="isProcessingSettings || !isDimensionReady('properties') || !isDimensionReady('connection') || !isDimensionReady('scopes') || isCheckingConnectionPermissions"
                                        class="d-inline-flex align-items-center gap-6"
                                        color="primary"
                                        mat-stroked-button
                                        type="button"
                                >
                                    <mat-icon>skip_next</mat-icon>
                                    Skip this step
                                </button>
                            </div>
                            @if (isDimensionReady('mapping')) {
                                <div class="banner">
                                    <mat-icon class="banner__icon status-check">check_circle</mat-icon>
                                    <div class="content d-flex flex-col gap-4">
                                        <div class="title">Field mapping set up successfully</div>
                                    </div>
                                </div>
                            }
                        </section>
                    </mat-step>
                    <mat-step [completed]="readiness.isOperationalReady" [editable]="true">
                        <ng-template matStepLabel>Completion</ng-template>
                        <section class="d-flex flex-col gap-16">
                            <div class="section-header">
                                <h4 class="m-0 f-s-16 f-w-600">Setup complete</h4>
                                <p class="m-0 f-s-14 text-gray-100">
                                    Congratulations! You have successfully set up the HubSpot integration. You can now
                                    start using Shine
                                    Enrich to synchronize your company data.
                                </p>
                            </div>
                        </section>
                    </mat-step>
                </mat-horizontal-stepper>
                }
            </mat-card-content>
        </mat-card>
    }
</div>
